import { buildRestApiUrl, buildFunctionUrl, getStandardHeaders } from '@/integrations/supabase/urlBuilder';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { BikeMovementWebhookPayload } from '@/types/webhookTypes';

interface MovementControlData {
  movement_blocked?: boolean;
  speed_limit?: number | null;
  last_movement_update?: string;
}

// Функция для отправки вебхука
const sendMovementWebhook = async (
  bikeData: any,
  action: 'block' | 'unblock' | 'speed_limit' | 'reset',
  changes: any
): Promise<void> => {
  try {
    console.log('=== ОТПРАВКА ВЕБХУКА УПРАВЛЕНИЯ ДВИЖЕНИЕМ ===');
    console.log('Данные велосипеда:', bikeData);
    console.log('Действие:', action);
    console.log('Изменения:', changes);
    
    // Получаем информацию о текущем пользователе
    const { data: { user } } = await supabase.auth.getUser();
    
    // Получаем дополнительную информацию о курьере, включая телефон и имя
    let courierPhone = null;
    let courierName = null;
    if (bikeData.currentRental?.courier_id) {
      console.log('Загружаем данные курьера для получения телефона и имени...');
      const { data: courierData, error: courierError } = await supabase
        .from('couriers')
        .select('phone, name')
        .eq('id', bikeData.currentRental.courier_id)
        .maybeSingle();
      
      if (courierData && !courierError) {
        courierPhone = courierData.phone;
        courierName = courierData.name;
        console.log('Найдены данные курьера:', { phone: courierPhone, name: courierName });
      } else {
        console.warn('Не удалось загрузить данные курьера:', courierError);
      }
    }
    
    const webhookPayload: BikeMovementWebhookPayload = {
      event_type: 'bike_movement_control_change',
      action,
      timestamp: new Date().toISOString(),
      bike: {
        id: bikeData.id,
        serial_number: bikeData.serialNumber || bikeData.serial_number || '',
        model: bikeData.model || '',
        gps_tag: bikeData.gps_tag || null,
        current_rental: bikeData.currentRental ? {
          id: bikeData.currentRental.id,
          courier_name: courierName || bikeData.currentRental.courierName || 'Неизвестный курьер',
          human_number: bikeData.currentRental.human_number || null,
          courier_phone: courierPhone,
        } : null,
      },
      changes,
      admin_info: {
        user_id: user?.id || 'unknown',
        timestamp: new Date().toISOString(),
      },
    };

    console.log('Полезная нагрузка вебхука:', JSON.stringify(webhookPayload, null, 2));

    // Вызываем Edge Function для отправки вебхука
    console.log('Вызываем Edge Function movement-webhook...');
    const { data, error } = await supabase.functions.invoke('movement-webhook', {
      body: webhookPayload,
    });

    if (error) {
      console.error('Ошибка при отправке вебхука:', error);
      toast.error(`Ошибка вебхука: ${error.message}`);
    } else {
      console.log('Вебхук успешно отправлен:', data);
      toast.success('Вебхук отправлен');
    }
  } catch (error) {
    console.error('Ошибка при подготовке вебхука:', error);
    toast.error(`Ошибка при отправке вебхука: ${error.message}`);
  }
};

// Функция для обновления управления движением велосипеда
export const updateBikeMovementControl = async (
  bikeId: string,
  data: MovementControlData
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('Обновляем управление движением для велосипеда:', bikeId, data);
    
    // Добавляем временную метку при каждом обновлении
    const updateData = {
      ...data,
      last_movement_update: new Date().toISOString()
    };
    
    const url = buildRestApiUrl(`bikes?id=eq.${bikeId}`);
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        ...getStandardHeaders(),
        'Prefer': 'return=minimal'
      },
      body: JSON.stringify(updateData)
    });
    
    if (!response.ok) {
      const errorData = await response.text();
      console.error('Ошибка ответа сервера:', errorData);
      throw new Error(`Ошибка при обновлении: ${response.status} ${errorData}`);
    }
    
    console.log('Управление движением успешно обновлено');
    return { success: true };
  } catch (error: any) {
    console.error('Ошибка при обновлении управления движением:', error);
    return { success: false, error: error.message };
  }
};

// Функция для блокировки/разблокировки движения
export const toggleBikeMovementBlock = async (
  bikeId: string,
  blocked: boolean
): Promise<{ success: boolean; error?: string }> => {
  console.log('=== БЛОКИРОВКА/РАЗБЛОКИРОВКА ДВИЖЕНИЯ ===');
  console.log('ID велосипеда:', bikeId);
  console.log('Блокировать:', blocked);

  // Получаем текущие данные велосипеда для вебхука
  let bikeData: any = {};
  let previousBlocked = false;
  try {
    const { data } = await supabase.from('bikes').select('*').eq('id', bikeId).single();
    bikeData = data || {};
    previousBlocked = data?.movement_blocked || false;
    console.log('Загружены данные велосипеда для вебхука:', bikeData);
  } catch (error) {
    console.warn('Не удалось загрузить данные велосипеда для вебхука:', error);
  }

  const data: MovementControlData = { 
    movement_blocked: blocked,
    // При блокировке сбрасываем ограничение скорости
    speed_limit: blocked ? null : undefined
  };
  
  const result = await updateBikeMovementControl(bikeId, data);
  
  if (result.success) {
    const action = blocked ? 'block' : 'unblock';
    toast.success(`Движение велосипеда ${blocked ? 'заблокировано' : 'разблокировано'}`);
    
    // Отправляем вебхук
    console.log('Отправляем вебхук для блокировки/разблокировки...');
    await sendMovementWebhook(bikeData, action, {
      movement_blocked: {
        previous: previousBlocked,
        current: blocked
      }
    });
  } else {
    toast.error(`Ошибка при ${blocked ? 'блокировке' : 'разблокировке'} движения: ${result.error}`);
  }
  
  return result;
};

// Функция для установки ограничения скорости
export const setBikeSpeedLimit = async (
  bikeId: string,
  speedLimit: number | null
): Promise<{ success: boolean; error?: string }> => {
  console.log('=== УСТАНОВКА ОГРАНИЧЕНИЯ СКОРОСТИ ===');
  console.log('ID велосипеда:', bikeId);
  console.log('Ограничение скорости:', speedLimit);

  // Получаем текущие данные велосипеда для вебхука
  let bikeData: any = {};
  let previousSpeedLimit: number | null = null;
  let previousBlocked = false;
  try {
    const { data } = await supabase.from('bikes').select('*').eq('id', bikeId).single();
    bikeData = data || {};
    previousSpeedLimit = data?.speed_limit || null;
    previousBlocked = data?.movement_blocked || false;
    console.log('Загружены данные велосипеда для вебхука:', bikeData);
  } catch (error) {
    console.warn('Не удалось загрузить данные велосипеда для вебхука:', error);
  }

  const data: MovementControlData = { 
    speed_limit: speedLimit,
    // При установке ограничения снимаем полную блокировку
    movement_blocked: false
  };
  
  const result = await updateBikeMovementControl(bikeId, data);
  
  if (result.success) {
    if (speedLimit) {
      toast.success(`Установлено ограничение скорости ${speedLimit} км/ч`);
    } else {
      toast.success('Ограничение скорости снято');
    }
    
    // Отправляем вебхук
    console.log('Отправляем вебхук для ограничения скорости...');
    await sendMovementWebhook(bikeData, 'speed_limit', {
      speed_limit: {
        previous: previousSpeedLimit,
        current: speedLimit
      },
      movement_blocked: {
        previous: previousBlocked,
        current: false
      }
    });
  } else {
    toast.error(`Ошибка при изменении ограничения скорости: ${result.error}`);
  }
  
  return result;
};

// Функция для сброса всех ограничений
export const resetBikeMovementRestrictions = async (
  bikeId: string
): Promise<{ success: boolean; error?: string }> => {
  console.log('=== СБРОС ВСЕХ ОГРАНИЧЕНИЙ ===');
  console.log('ID велосипеда:', bikeId);

  // Получаем текущие данные велосипеда для вебхука
  let bikeData: any = {};
  let previousState: any = {};
  try {
    const { data } = await supabase.from('bikes').select('*').eq('id', bikeId).single();
    bikeData = data || {};
    previousState = {
      movement_blocked: data?.movement_blocked || false,
      speed_limit: data?.speed_limit || null
    };
    console.log('Загружены данные велосипеда для вебхука:', bikeData);
  } catch (error) {
    console.warn('Не удалось загрузить данные велосипеда для вебхука:', error);
  }

  const data: MovementControlData = { 
    movement_blocked: false,
    speed_limit: null
  };
  
  const result = await updateBikeMovementControl(bikeId, data);
  
  if (result.success) {
    toast.success('Все ограничения движения сброшены');
    
    // Отправляем вебхук
    console.log('Отправляем вебхук для сброса ограничений...');
    await sendMovementWebhook(bikeData, 'reset', {
      movement_blocked: {
        previous: previousState.movement_blocked,
        current: false
      },
      speed_limit: {
        previous: previousState.speed_limit,
        current: null
      }
    });
  } else {
    toast.error(`Ошибка при сбросе ограничений: ${result.error}`);
  }
  
  return result;
};
